import React, { useCallback, useState } from "react";
import { Modal, Button, Slider } from "antd";
import Cropper from "react-easy-crop";
import { AI_THEME, aiPrimaryButtonStyle } from "./faceSearchTheme";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImageFile = async (imageSrc, pixelCrop, fileName, mimeType) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], fileName, { type: mimeType }));
      },
      mimeType,
      0.92,
    );
  });
};

const FacePhotoCropModal = ({
  open,
  imageSrc,
  fileName,
  mimeType,
  onApply,
  onUseOriginal,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  const handleCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;

    setApplying(true);
    try {
      const croppedFile = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels,
        fileName.replace(/(\.[^.]+)?$/, "_cropped$1"),
        mimeType,
      );
      if (croppedFile) {
        onApply?.(croppedFile);
      }
    } finally {
      setApplying(false);
    }
  };

  const handleAfterClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setApplying(false);
  };

  return (
    <Modal
      title="Crop photo (optional)"
      open={open}
      visible={open}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      width={560}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="original" onClick={onUseOriginal}>
          Use original
        </Button>,
        <Button
          key="apply"
          type="primary"
          loading={applying}
          onClick={handleApplyCrop}
          style={aiPrimaryButtonStyle}
        >
          Apply crop
        </Button>,
      ]}
    >
      <p style={{ color: AI_THEME.muted, marginBottom: 12, fontSize: 13 }}>
        Adjust the crop area or use the original photo without cropping.
      </p>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 320,
          background: "#111",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        )}
      </div>
      <div style={{ marginTop: 16 }}>
        <span style={{ fontSize: 13, color: AI_THEME.muted }}>Zoom</span>
        <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
      </div>
    </Modal>
  );
};

export default FacePhotoCropModal;
